import { l } from '@manychat/manyui'

import { useAppSelector } from 'reduxTyped'
import { contentManagementSelectors } from 'apps/cms/store'
import { FlowPickerSource } from 'apps/flowPicker/lib/constants'
import { FlowPickerSelectors } from 'apps/flowPicker/store'
import { isExternalFlowOrigin } from 'common/flow/flowOrigin'
import { ICON_PRODUCT_NAME } from 'common/fusion/constants'
import { FsFlow } from 'shared/api/requests/cms/schemas'

interface FlowValidationResult {
  isValid: boolean
  errorMessage: string
}

export function useFlowValidation(flow: FsFlow | undefined): FlowValidationResult {
  const { disableFlowCard: shouldValidateFlow } = useAppSelector(contentManagementSelectors.getUi)
  const source = useAppSelector(FlowPickerSelectors.getFlowPickerSource)

  if (!shouldValidateFlow) {
    return { isValid: true, errorMessage: '' }
  }

  if (!flow) {
    return {
      isValid: false,
      errorMessage: l.translate('Flow not found.'),
    }
  }

  if (isExternalFlowOrigin(flow.origin)) {
    return {
      isValid: false,
      errorMessage: l.translate(
        'Automations created with {productName} cannot be managed in Classic.',
        {
          productName: ICON_PRODUCT_NAME,
        },
      ),
    }
  }

  if (!flow.has_published_content) {
    return {
      isValid: false,
      errorMessage: l.translate(
        'This Automation doesn’t have a live version. Set it live before choosing.',
      ),
    }
  }

  if (source === FlowPickerSource.BROADCAST && flow.channels_usage.tiktok) {
    return {
      isValid: false,
      errorMessage: l.translate(
        'You can’t pick this. Broadcasts don’t allow automations for TikTok.',
      ),
    }
  }

  if (source && source !== FlowPickerSource.LIVE_CHAT && flow.easy_builder) {
    return {
      isValid: false,
      errorMessage: l.translate('You can’t pick this. Quick Automations aren’t compatible.'),
    }
  }

  const accessData = flow.template_installation_access_data
  if (!flow.easy_builder && accessData?.is_protected && !accessData.is_owner) {
    if (accessData.is_disabled || accessData.is_locked) {
      return {
        isValid: false,
        errorMessage: l.translate(
          'This item is a part of a locked Template which means it is locked from viewing and editing by the Template owner.',
        ),
      }
    }
  }

  return { isValid: true, errorMessage: '' }
}
